import React from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Mail, Phone, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  POLICY_LINKS,
  REGIONS,
  REGION_ORDER,
  LAST_UPDATED,
  resolveRegion,
  resolvePolicySlug,
  policyHref,
} from "@/lib/policies";

/**
 * Renders one legal document for one jurisdiction.
 *
 * Mounted once per jurisdiction in App.js — unprefixed for India
 * (`/policy/:slug`), `/ae` for the UAE and `/np` for Nepal — so every market's
 * documents share one domain and each has a single stable public URL to paste
 * into Play Console and App Store Connect.
 */
export default function PolicyPage({ region: regionProp }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const region = resolveRegion(regionProp);
  const canonical = resolvePolicySlug(slug);
  const policy = canonical ? region.policies[canonical] : null;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, region.key]);

  React.useEffect(() => {
    if (!policy) return;
    const previous = document.title;
    document.title = `${policy.title} · Wateriia ${region.label}`;
    return () => {
      document.title = previous;
    };
  }, [policy, region.label]);

  // A legacy or shortened slug redirects to its canonical URL.
  if (canonical && canonical !== slug) {
    return <Navigate to={policyHref(region.key, canonical)} replace />;
  }

  if (!policy) {
    return (
      <>
        <Navbar />
        <main className="water-grad min-h-screen">
          <div className="container-x pt-32 md:pt-40 pb-24 text-center">
            <div className="eyebrow">404</div>
            <h1 className="h-display text-4xl md:text-5xl mt-3">Policy not found.</h1>
            <p className="mt-4 text-slate-500 max-w-lg mx-auto">
              That document doesn't exist or has moved. Here is everything we publish for{" "}
              {region.label}:
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {POLICY_LINKS.map((p) => (
                <Link
                  key={p.slug}
                  to={policyHref(region.key, p.slug)}
                  className="rounded-full glass px-4 py-2 text-sm text-slate-700 hover:text-[#0369a1] transition"
                >
                  {p.label}
                </Link>
              ))}
            </div>
            <button
              onClick={() => navigate(`${region.base}/`)}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#0b1d2a] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0369a1] transition"
            >
              <ArrowLeft size={16} /> Back to home
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main className="water-grad">
        <div className="container-x pt-28 md:pt-36 pb-20">
          {/* breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="flex flex-wrap items-center gap-2 text-xs text-slate-500"
          >
            <Link to="/" className="link-x hover:text-[#0369a1]">
              Home
            </Link>
            <span className="text-slate-300">/</span>
            <span>Legal</span>
            <span className="text-slate-300">/</span>
            <span>{region.label}</span>
            <span className="text-slate-300">/</span>
            <span className="text-[#0b1d2a] font-medium">{policy.title}</span>
          </nav>

          <div className="mt-6 flex items-start gap-4">
            <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#0b1d2a] text-white">
              <FileText size={20} />
            </div>
            <div>
              <div className="eyebrow">
                Legal · Wateriia {region.label} · {region.currency}
              </div>
              <h1
                data-testid="policy-title"
                className="h-display text-4xl sm:text-5xl lg:text-6xl mt-2"
              >
                {policy.title}
              </h1>
            </div>
          </div>

          <p className="mt-5 max-w-2xl text-lg text-slate-600">{policy.subtitle}</p>

          {/* Jurisdiction switcher — the same document under the other market's law */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              Jurisdiction
            </span>
            <div className="inline-flex rounded-full glass p-1">
              {REGION_ORDER.map((key) => {
                const r = REGIONS[key];
                const active = key === region.key;
                return (
                  <Link
                    key={key}
                    data-testid={`policy-region-${key}`}
                    to={policyHref(key, canonical)}
                    aria-current={active ? "page" : undefined}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                      active
                        ? "bg-[#0b1d2a] text-white"
                        : "text-slate-600 hover:text-[#0369a1]"
                    }`}
                  >
                    {r.label}
                  </Link>
                );
              })}
            </div>
            <span className="text-xs text-slate-500">
              Governed by {region.law}
            </span>
          </div>

          <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-slate-500">
            Last updated · {LAST_UPDATED}
          </div>

          <hr className="my-10 line-soft border-t" />

          <div className="grid lg:grid-cols-12 gap-10">
            {/* Document */}
            <article className="lg:col-span-8">
              {policy.storeNote && (
                <p
                  data-testid="policy-store-note"
                  className="mb-8 rounded-2xl border border-sky-100 bg-white/70 px-5 py-4 text-sm text-slate-600"
                >
                  {policy.storeNote}
                </p>
              )}

              <div className="space-y-9">
                {policy.sections.map((s, i) => (
                  <section key={i} data-testid={`policy-section-${i}`}>
                    <h2 className="h-display text-xl md:text-2xl text-[#0b1d2a]">{s.h}</h2>
                    {s.p && <p className="mt-3 text-slate-600 leading-relaxed">{s.p}</p>}
                    {s.list && (
                      <ul className="mt-3 space-y-2.5">
                        {s.list.map((item, j) => (
                          <li
                            key={j}
                            className="relative pl-5 text-slate-600 leading-relaxed before:absolute before:left-0 before:top-[0.62em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#0ea5e9]"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.note && (
                      <p className="mt-4 rounded-xl bg-white/70 border border-sky-100 px-4 py-3 text-sm text-slate-500 leading-relaxed">
                        {s.note}
                      </p>
                    )}
                  </section>
                ))}
              </div>

              {/* Contact block */}
              <div className="mt-14 rounded-3xl glass p-8">
                <div className="eyebrow">Questions?</div>
                <h3 className="h-display text-xl md:text-2xl mt-2 text-[#0b1d2a]">
                  Talk to the Wateriia team.
                </h3>
                <ul className="mt-4 space-y-2 text-sm text-slate-600">
                  <li>
                    <a
                      href={`mailto:${region.contact.email}`}
                      className="inline-flex items-center gap-2 link-x text-[#0369a1] font-medium"
                    >
                      <Mail size={15} /> {region.contact.email}
                    </a>
                  </li>
                  <li>
                    <a
                      href={`tel:${region.contact.phoneTel}`}
                      className="inline-flex items-center gap-2 link-x text-[#0369a1] font-medium"
                    >
                      <Phone size={15} /> {region.contact.phoneDisplay}
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <MapPin size={15} className="mt-0.5 shrink-0 text-slate-400" />
                    <span>
                      {region.contact.legal} · {region.contact.address}
                    </span>
                  </li>
                </ul>
              </div>

              {region.siteUrl && (
                <p className="mt-4 text-xs text-slate-500">
                  Wateriia {region.label} also publishes these documents on its own
                  site at{" "}
                  <a
                    href={region.siteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="link-x font-medium text-[#0369a1]"
                  >
                    {region.siteUrl.replace(/^https?:\/\//, "")}
                  </a>
                  . The text is the same.
                </p>
              )}

              <button
                onClick={() => navigate(`${region.base}/`)}
                className="mt-10 inline-flex items-center gap-2 rounded-full border border-[#0b1d2a] px-5 py-2.5 text-sm font-semibold text-[#0b1d2a] hover:bg-[#0b1d2a] hover:text-white transition"
              >
                <ArrowLeft size={16} /> Back to home
              </button>
            </article>

            {/* Index of every legal document in this jurisdiction */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 rounded-3xl glass p-6">
                <div className="eyebrow">All policies · {region.label}</div>
                <nav className="mt-4 space-y-1">
                  {POLICY_LINKS.map((p) => {
                    const active = p.slug === canonical;
                    return (
                      <Link
                        key={p.slug}
                        data-testid={`policy-nav-${p.slug}`}
                        to={policyHref(region.key, p.slug)}
                        aria-current={active ? "page" : undefined}
                        className={`block rounded-lg px-3 py-2 text-sm transition ${
                          active
                            ? "bg-white text-[#0369a1] font-semibold shadow-sm"
                            : "text-slate-600 hover:bg-white/70 hover:text-[#0b1d2a]"
                        }`}
                      >
                        {p.label}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}
